package dev.lingvo.site.components.editor

import androidx.compose.runtime.Stable
import androidx.compose.runtime.mutableStateMapOf
import dev.lingvo.shared.CommentDto
import dev.lingvo.shared.CreateCommentRequest
import dev.lingvo.shared.HistoryEntryDto
import dev.lingvo.shared.KeyHistoryPageDto
import dev.lingvo.shared.KeyRow
import dev.lingvo.shared.MtProvider
import dev.lingvo.shared.MtTranslateRequest
import dev.lingvo.shared.MtTranslateResponse
import dev.lingvo.shared.SuggestionDto
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.ApiException
import dev.lingvo.site.api.Async
import dev.lingvo.site.ds.LToastVariant
import dev.lingvo.site.ds.ToastState
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.launch

@Stable
internal class EditorPanelStore(
    private val projectSlug: String,
    private val locale: String,
    private val scope: CoroutineScope,
    private val toasts: ToastState,
    private val canWriteTranslations: () -> Boolean,
    private val currentKeyId: () -> String?,
    private val shouldToastAssistOutcomes: () -> Boolean = { true },
    private val assistStatuses: EditorAssistStatusStore = EditorAssistStatusStore(),
    private val suggestionsRequest: suspend (keyId: String) -> List<SuggestionDto> = { keyId ->
        ApiClient.listSuggestions(projectSlug, keyId, locale)
    },
    private val translateRequest: suspend (EditorGenerationClaim) -> MtTranslateResponse = { claim ->
        ApiClient.translate(
            projectSlug = claim.projectSlug,
            request = MtTranslateRequest(
                provider = claim.provider,
                locale = claim.locale,
                keyIds = listOf(claim.keyId),
                overwrite = true,
                save = false,
            ),
        )
    },
    private val matchedSourceRequest: suspend (sourceKeyName: String) -> String? = { sourceKeyName ->
        ApiClient.listKeys(
            projectSlug = projectSlug,
            locale = locale,
            search = sourceKeyName,
            limit = 10,
        ).items.firstOrNull { it.key.name == sourceKeyName }?.sourceText()
    },
) {
    private val assist = EditorAssistAsyncStore(
        projectSlug = projectSlug,
        locale = locale,
        scope = scope,
        toasts = toasts,
        canWriteTranslations = canWriteTranslations,
        currentKeyId = currentKeyId,
        shouldToastAssistOutcomes = shouldToastAssistOutcomes,
        assistStatuses = assistStatuses,
        suggestionsRequest = suggestionsRequest,
        translateRequest = translateRequest,
        matchedSourceRequest = matchedSourceRequest,
    )

    val suggestions = assist.suggestions
    val suggestionLoads = assist.suggestionLoads
    val machineSuggestions = assist.machineSuggestions
    val matchedSources = assist.matchedSources
    val generationStatuses = assist.generationStatuses
    val translatingProviders: Map<String, Set<MtProvider>>
        get() = assist.translatingProviders

    val comments = mutableStateMapOf<String, Async<List<CommentDto>>>()
    val commentResolution = EditorCommentResolutionStore(projectSlug, scope, canWriteTranslations, currentKeyId, comments)
    val history = mutableStateMapOf<String, Async<KeyHistoryPageDto>>()

    /** Keys whose next page is in flight, so the control cannot be pressed twice. */
    val historyLoadingMore = mutableStateMapOf<String, Boolean>()
    val postingComments = mutableStateMapOf<String, Boolean>()

    fun ensureAssistLoaded(row: KeyRow): Job? = assist.ensureLoaded(row)

    fun retryAssist(row: KeyRow, expectedFailure: Async.Failure): Job? =
        assist.retry(row, expectedFailure)

    internal fun beginSuggestionLoad(row: KeyRow): EditorSuggestionLoadRequest? =
        assist.beginSuggestionLoad(row)

    internal suspend fun executeSuggestionLoad(request: EditorSuggestionLoadRequest) {
        assist.executeSuggestionLoad(request)
    }

    internal fun activeSuggestionStatusPublicationId(keyId: String): Long? =
        assist.activeSuggestionStatusPublicationId(keyId)

    fun rankedSuggestions(row: KeyRow): List<EditorSuggestion> = assist.rankedSuggestions(row)

    fun translate(row: KeyRow, provider: MtProvider): Job? = assist.translate(row, provider)

    internal fun beginGeneration(
        row: KeyRow,
        provider: MtProvider,
    ): EditorGenerationRequest? = assist.beginGeneration(row, provider)

    internal suspend fun executeGeneration(request: EditorGenerationRequest) {
        assist.executeGeneration(request)
    }

    fun postComment(keyId: String, body: String, onPosted: () -> Unit) {
        if (!canWriteTranslations()) return
        if (body.isBlank() || postingComments[keyId] == true) return
        postingComments[keyId] = true
        scope.launch {
            try {
                val created = ApiClient.createKeyComment(
                    projectSlug,
                    keyId,
                    CreateCommentRequest(body = body.trim(), locale = locale),
                )
                // **Only append to a list that was actually loaded.** The composer renders outside
                // the panel's `when`, so "Post comment" is live while an `Async.Failure` is on
                // screen. Coercing that failure with `?.value.orEmpty()` produced
                // `Async.Success(listOf(created))` — every existing comment on the key vanished
                // along with the error and its retry, and the reader was shown a confident record
                // of one comment where the truth was "we do not know what is here".
                when (val current = comments[keyId]) {
                    is Async.Success -> comments[keyId] = Async.Success(current.value + created)
                    else -> {
                        comments.remove(keyId)
                        ensureDiscussLoaded(keyId)
                    }
                }
                onPosted()
            } catch (cancelled: CancellationException) {
                throw cancelled
            } catch (failure: Throwable) {
                toasts.show(
                    failure.message?.takeIf(String::isNotBlank)
                        ?: "Couldn't post this comment. Retry?",
                    LToastVariant.DANGER,
                )
            } finally {
                postingComments[keyId] = false
            }
        }
    }

    /**
     * Fold a save's new version into the panel instead of rebuilding it.
     *
     * This used to drop the key and re-fetch page one. Everything past the first fifty rows went
     * with it: somebody who had pressed "Show older changes" twice held a hundred and fifty rows and
     * lost a hundred of them to their own save, and the card they were reading was replaced by four
     * skeletons at the exact moment their edit made them want to look at it. `loadOlderHistory`
     * already refuses to lose rows a person is reading when a request fails; a successful save is
     * not a better reason to do it.
     *
     * Merging by id rather than by position, because a colleague's save can land between the two
     * requests and shift everything down by one. `nextCursor` is left alone: the older pages are
     * still loaded, so the cursor that pages past them is still the right one.
     */
    fun refreshHistory(keyId: String) {
        when (history[keyId]) {
            // Never opened, or a fetch is already in flight that will carry this save anyway.
            null, Async.Loading -> return
            // Nothing on screen to preserve, so the cheap path is also the correct one.
            is Async.Failure -> {
                history.remove(keyId)
                ensureActivityLoaded(keyId)
                return
            }

            is Async.Success -> Unit
        }
        scope.launch {
            val page = try {
                ApiClient.getKeyHistory(projectSlug, keyId, locale)
            } catch (cancelled: CancellationException) {
                throw cancelled
            } catch (_: Throwable) {
                // The panel keeps what it has. A save that succeeded must not make the record on
                // screen look worse than it did before.
                return@launch
            }
            val current = history[keyId] as? Async.Success ?: return@launch
            val known = current.value.entries.mapTo(mutableSetOf(), HistoryEntryDto::id)
            val fresh = page.entries.filterNot { it.id in known }
            history[keyId] = Async.Success(
                current.value.copy(entries = fresh + current.value.entries),
            )
        }
    }

    internal suspend fun loadMatchedSources(keyId: String, items: List<SuggestionDto>) {
        assist.loadMatchedSources(keyId, items)
    }

    fun ensureDiscussLoaded(keyId: String) {
        if (comments[keyId] != null) return
        comments[keyId] = Async.Loading
        scope.launch {
            comments[keyId] = try {
                Async.Success(ApiClient.listKeyComments(projectSlug, keyId))
            } catch (cancelled: CancellationException) {
                throw cancelled
            } catch (failure: Throwable) {
                // The status travels, the same way `loadAsync` carries it. Without it a colleague
                // deleting this key, or a demotion mid-session, answered the panel with a red alert
                // and a Retry that fails identically for ever — the exact case `AsyncFailureState`
                // exists to tell apart.
                Async.Failure(
                    message = failure.message?.takeIf(String::isNotBlank)
                        ?: "Couldn't load comments. Retry?",
                    status = (failure as? ApiException)?.status,
                )
            }
        }
    }

    fun ensureActivityLoaded(keyId: String) {
        if (history[keyId] != null) return
        history[keyId] = Async.Loading
        scope.launch {
            history[keyId] = try {
                Async.Success(ApiClient.getKeyHistory(projectSlug, keyId, locale))
            } catch (cancelled: CancellationException) {
                throw cancelled
            } catch (failure: Throwable) {
                Async.Failure(
                    message = failure.message?.takeIf(String::isNotBlank)
                        ?: "Couldn't load history. Retry?",
                    status = (failure as? ApiException)?.status,
                )
            }
        }
    }

    /**
     * Append the next page.
     *
     * Failure leaves what is already on screen alone and clears the in-flight flag: losing fifty
     * rows a person is reading because the fifty-first request failed would be a worse outcome than
     * the missing page.
     */
    fun loadOlderHistory(keyId: String) {
        val loaded = history[keyId] as? Async.Success ?: return
        val cursor = loaded.value.nextCursor ?: return
        if (historyLoadingMore[keyId] == true) return
        historyLoadingMore[keyId] = true
        scope.launch {
            try {
                val next = ApiClient.getKeyHistory(projectSlug, keyId, locale, cursor)
                val current = history[keyId] as? Async.Success ?: return@launch
                history[keyId] = Async.Success(
                    current.value.copy(
                        entries = current.value.entries + next.entries,
                        nextCursor = next.nextCursor,
                    ),
                )
            } catch (cancelled: CancellationException) {
                throw cancelled
            } catch (_: Throwable) {
                // Deliberately silent on the store; the panel keeps what it has and the control
                // stays available to try again.
            } finally {
                historyLoadingMore[keyId] = false
            }
        }
    }
}
